import React, {PropsWithChildren} from "react";
import {observer} from "mobx-react";
import {NullableStation} from "../../../../../services/stations/station.service.interface";
import {EditPencilIcon} from "../../../../../assets/svg/edit-pencil.icon";
import {
    IBookingSearchResultRecordViewModel
} from "../../../../../services/agency-booking-search/booking-search-result-record.view-model.interface";
import styled, {css} from "styled-components";
import {BookingStatusEnum} from "../../../../../services/dot-rez-api/data-contracts/enums/booking-status.enum";
import {CellBox} from "./cell.box";
import {useServices} from "../../../../../hooks/use-services.hook";
import {
    IBookingSearchStrategyViewModel
} from "../../../../../services/agency-booking-search/strategies/booking-search.strategy.interface";

const TableCellBox = styled(CellBox)<{$isOnSelectedRow: boolean; $isOnHoverRow: boolean}>`
    cursor: pointer;
    ${props => props.$isOnHoverRow && css`
        background-color: ${props => props.theme.colors.secondary};
        color: ${props => props.theme.colors.secondaryContrast};
    `}
    
    ${props => props.$isOnSelectedRow && css`
        background-color: ${props => props.theme.colors.primary};
        color: ${props => props.theme.colors.primaryContrast};
    `}
   
`
interface TableCellComponentProps extends PropsWithChildren {
    isOnSelectedRow: boolean;
    isOnHoverRow: boolean;
    onClick: () => void;
    onHoverChanged: (isHover: boolean) => void;
    className?: string;
}

const TableCellComponent: React.FC<TableCellComponentProps> = observer((props) => {
    return (
        <TableCellBox $isOnSelectedRow={props.isOnSelectedRow}
                      $isOnHoverRow={props.isOnHoverRow}
                      className={props.className}
                      onClick={props.onClick}
                      onMouseEnter={() => props.onHoverChanged(true)}
                      onMouseLeave={() => props.onHoverChanged(false)}>
            {props.children}
        </TableCellBox>
    )
})

const CenteredTableCellComponent = styled(TableCellComponent)`
    justify-content: center;
`


const ModifyButtonCell = styled(CenteredTableCellComponent)`
    width: 100%;
    gap: 4px;
    font-weight: ${props => props.theme.fontWeight.semiBold};

    &:hover {
        text-decoration: underline;
    }
    
    ${props => !props.isOnSelectedRow && !props.isOnHoverRow && css`
        color: ${props => props.theme.colors.mediumShade};
        &:hover {
            color: ${props => props.theme.colors.dark};
        }
    `}
`

interface ModifyButtonComponentProps extends TableCellComponentProps {
    record: IBookingSearchResultRecordViewModel;
}

const ModifyButtonComponent: React.FC<ModifyButtonComponentProps> = observer((props) => {
    const services = useServices();
    const onModifyClick = async () => {
        if(!props.isOnSelectedRow) {
            props.onClick();
        }

        await props.record.retrieveBooking();
    }


    return (
        <ModifyButtonCell {...props} onClick={onModifyClick}>
            <div>
                <EditPencilIcon size={0.6}/>
            </div>
            <span>
                {services.language.translate('Modify')}
            </span>
        </ModifyButtonCell>
    )
})

const FlightDateComponent = styled(TableCellComponent)`
    flex-direction: column;
`

const BookingStatusHoldComponent = styled(CenteredTableCellComponent)`
    color: ${props => props.theme.colors.dangerShade};
    font-weight: ${props => props.theme.fontWeight.bold};
`

const BookingStatusConfirmedComponent = styled(CenteredTableCellComponent)`
    ${props => !props.isOnSelectedRow && !props.isOnHoverRow && css`
        color: ${props => props.theme.colors.successShade};
    `}
`

interface BookingStatusColumnComponentProps extends TableCellComponentProps{
    record: IBookingSearchResultRecordViewModel;
}

const BookingStatusCellComponent: React.FC<BookingStatusColumnComponentProps> = observer((props) => {
    if(props.record.bookingStatus === BookingStatusEnum.Hold) {
        return (
            <BookingStatusHoldComponent {...props}>
                {props.record.bookingStatusName}
            </BookingStatusHoldComponent>
        )
    }

    if(props.record.bookingStatus === BookingStatusEnum.Confirmed) {
        return (
            <BookingStatusConfirmedComponent {...props}>
                {props.record.bookingStatusName}
            </BookingStatusConfirmedComponent>
        )
    }

    return (
        <CenteredTableCellComponent {...props}>{props.record.bookingStatusName}</CenteredTableCellComponent>
    )
})




function formatStationName(station: NullableStation){
    if(!station) {
        return "";
    }

    return `${station.stationName} (${station.stationCode})`
}

interface TableRowComponentProps {
    record: IBookingSearchResultRecordViewModel;
    recordIndex: number;
    currentStrategy: IBookingSearchStrategyViewModel;
    onRowClick: (recordKey: string) => void;
}

export const TableRowComponent: React.FC<TableRowComponentProps> = observer((props) => {

    const [isHover, setIsHover] = React.useState<boolean>(false);

    const isCurrentSelectedRow = props.currentStrategy.selectedRecordKey === props.record.uniqueKey;

    const onCellClick = () => {
        props.onRowClick(props.record.uniqueKey);
    }

    const cellProps: TableCellComponentProps = {
        isOnSelectedRow: isCurrentSelectedRow,
        isOnHoverRow: isHover,
        onHoverChanged: setIsHover,
        onClick: onCellClick
    }

    const renderModifyButton = () => {
        if(props.record.canBeModified) {
            return (
                <ModifyButtonComponent {...cellProps} record={props.record}/>
            );
        } else {
            return (
                <TableCellComponent {...cellProps}/>
            );
        }
    }

    return (
        <>
            {renderModifyButton()}
            <CenteredTableCellComponent {...cellProps}>
                {props.currentStrategy.getRecordPosition(props.recordIndex)}
            </CenteredTableCellComponent>
            <CenteredTableCellComponent {...cellProps}>
                {props.record.pnr}
            </CenteredTableCellComponent>
            <TableCellComponent {...cellProps}>{formatStationName(props.record.origin)}</TableCellComponent>
            <TableCellComponent {...cellProps}>{formatStationName(props.record.destination)}</TableCellComponent>
            <CenteredTableCellComponent {...cellProps}>{props.record.flightNumber}</CenteredTableCellComponent>
            <FlightDateComponent {...cellProps}>
                <div>{props.record.flightDepartureDate}</div>
                <div>{props.record.flightDepartureTime}</div>
            </FlightDateComponent>
            <TableCellComponent {...cellProps}>{props.record.passenger}</TableCellComponent>
            <BookingStatusCellComponent {...cellProps} record={props.record}/>
            <TableCellComponent {...cellProps}>{props.record.agent}</TableCellComponent>
        </>
    )
})